// src/components/unified/UnifiedNavbar.jsx
import { Menu, User, LogOut, Settings, Download } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useState } from "react";
import NotificationBell from "../NotificationBell";

export default function UnifiedNavbar({ user, onLogout, isMobile, onMenuToggle, pageTitle, sidebarOpen }) {
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Determine layout mode
  const isDesktop = window.innerWidth >= 1024;
  const isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;

  // Get page-specific title for mobile if not provided
  const getMobilePageTitle = () => {
    if (pageTitle) return pageTitle;

    if (location.pathname.includes('/admin/')) {
      if (location.pathname.includes('asset-manager')) return 'Asset Manager';
      if (location.pathname.includes('disposed-assets')) return 'Disposed Assets';
      if (location.pathname.includes('user-administration')) return 'User Administration';
      if (location.pathname.includes('request-approval')) return 'Request Approval';
      if (location.pathname.includes('map-editor')) return 'Map Editor';
      if (location.pathname.includes('qr-tools')) return 'QR Tools';
      if (location.pathname.includes('end-week-report')) return 'End Week Report';
      if (location.pathname.includes('settings')) return 'Settings';
      return 'Admin Dashboard';
    }

    if (location.pathname.includes('/technician/scan')) return 'Scan Asset QR';
    if (location.pathname.includes('/technician/reports')) return 'Reports';
    if (location.pathname.includes('/technician/settings')) return 'My Profile';
    if (location.pathname.includes('/technician/tasks')) return 'Tasks';
    if (location.pathname.includes('/technician/task/')) return 'Task Details';

    return 'CATHS Asset Management';
  };

  const handleLogout = () => {
    setShowUserMenu(false);
    onLogout();
  };

  return (
    <div
      className={`fixed top-0 z-30 bg-white shadow-sm border-b border-gray-200 px-4 lg:px-6 py-3 lg:py-4 flex items-center justify-between transition-all duration-300 ${
        isDesktop ? (sidebarOpen ? 'left-64 right-0' : 'left-0 right-0') :
        'left-0 right-0'
      }`}
    >
      {/* Left section: Toggle button and branding */}
      <div className="flex items-center flex-1 min-w-0">
        {/* Hamburger menu - always visible */}
        <button
          onClick={(e) => {
            e.preventDefault();
            onMenuToggle();
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            onMenuToggle();
          }}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors mr-3 lg:mr-4 flex-shrink-0 touch-manipulation select-none"
        >
          <Menu className="w-5 h-5 text-gray-600" />
        </button>

        <img
          src="/cathslogonobg.png"
          alt="CATHS Logo"
          className="h-8 w-8 mr-3 flex-shrink-0"
        />

        <h1 className="text-lg lg:text-xl font-bold text-gray-900 truncate">
          {isDesktop ? "CATHS Asset Management System" :
           isTablet ? (sidebarOpen ? "CATHS Asset Management System" : getMobilePageTitle()) :
           (sidebarOpen ? "CATHS Asset Management System" : getMobilePageTitle())}
        </h1>
      </div>

      {/* Right section: User actions */}
      <div className="flex items-center space-x-3 ml-4 flex-shrink-0">
        {/* Notification Bell */}
        <NotificationBell />

        {/* User profile dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 text-white" />
            </div>
            {isDesktop && (
              <span className="text-sm font-medium text-gray-700 max-w-20 truncate">
                {user?.name || 'Admin'}
              </span>
            )}
          </button>

          {/* Dropdown menu */}
          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>

              <a
                href={`/${user?.role || 'admin'}/settings`}
                onClick={() => setShowUserMenu(false)}
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Settings className="w-4 h-4 mr-3" />
                Account Settings
              </a>

              <a
                href="https://drive.google.com/file/d/1pkeYZLBS3RsnH3udqpowWbpgu9ZnES-5/view?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowUserMenu(false)}
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Download className="w-4 h-4 mr-3" />
                Download App
              </a>

              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-3" />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Overlay to close dropdown when clicking outside */}
      {showUserMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowUserMenu(false)}
        />
      )}
    </div>
  );
}
